import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { FilePlus, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { requireAuth } from "@/middleware/auth";
import { useSuggestQuery } from "@/queries/suggest";

export const Route = createFileRoute("/support/")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const queryOptions = useSuggestQuery();
	const { data, isLoading, error } = useQuery(queryOptions);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-full">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="size-12 animate-spin text-blue-500" />
					<p className="text-lg text-gray-600 font-medium">
						문의 목록을 불러오는 중...
					</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center h-full">
				<div className="flex flex-col items-center gap-4">
					<div className="size-16 bg-red-100 rounded-full flex items-center justify-center">
						<FilePlus className="size-8 text-red-500" />
					</div>
					<p className="text-lg text-gray-600 font-medium">
						문의 목록을 불러올 수 없습니다.
					</p>
					<Button onClick={() => window.location.reload()}>다시 시도</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1 container mx-auto px-4">
			<div className="flex justify-end items-center py-4 ml-3">
				<Link to="/support/submit">
					<Button className="flex items-center gap-2">
						<FilePlus className="size-4" />새 문의 작성
					</Button>
				</Link>
			</div>

			{data && data.length > 0 ? (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-20">번호</TableHead>
							<TableHead>제목</TableHead>
							<TableHead>내용</TableHead>
							<TableHead>파일</TableHead>
							<TableHead className="w-32">제출일시</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{data.map((suggest) => (
							<TableRow key={suggest.no}>
								<TableCell className="font-medium">{suggest.no}</TableCell>
								<TableCell className="font-medium">{suggest.name}</TableCell>
								<TableCell className="max-w-md">
									<div className="truncate" title={suggest.content}>
										{suggest.content}
									</div>
								</TableCell>
								<TableCell>
									{suggest.file_url ? (
										<a
											href={suggest.file_url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-blue-600 hover:text-blue-800 underline"
										>
											파일 보기
										</a>
									) : (
										<span className="text-gray-400">-</span>
									)}
								</TableCell>
								<TableCell>
									{format(new Date(suggest.sent_at), "yyyy-MM-dd HH:mm:ss")}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			) : (
				<div className="flex flex-col items-center justify-center py-20">
					<div className="size-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
						<FilePlus className="size-12 text-gray-400" />
					</div>
					<h3 className="text-2xl font-bold text-gray-900 mb-2">
						아직 제출한 문의가 없습니다
					</h3>
					<p className="text-gray-600 text-center max-w-md">
						첫 번째 문의를 작성해보세요.
					</p>
				</div>
			)}
		</div>
	);
}
